import { Component, computed, effect, EventEmitter, inject, input, Output, Signal, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, finalize, map, startWith } from 'rxjs';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CurrencyService, CurrencyInputFieldComponent, CurrencyFormatPipe } from '@zayan-erp/shared';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { RadioButtonModule } from '@openng/optimus-ui/radiobutton';
import { DsmLotIncentiveService } from '../../../services/dsm-lot-incentive.service';
import { LotAddendumIncentiveApplicationMethod } from '../../../enums/dsm-asp-incentives.enums';
import { DsmAddendumIncentivePreviewComponent } from '../dsm-addendum-incentive-preview/dsm-addendum-incentive-preview.component';
import { DsmApplyAddendumIncentivePayload } from '../../../models/dsm-lot-incentives.models';

@Component({
  selector: 'dsm-addendum-incentive-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CurrencyInputFieldComponent,
    ButtonModule,
    TextareaModule,
    RadioButtonModule,
    CurrencyFormatPipe,
    DsmAddendumIncentivePreviewComponent
  ],
  templateUrl: './dsm-addendum-incentive-form.component.html'
})
export class DsmAddendumIncentiveFormComponent {
  private readonly fb = inject(FormBuilder);
  private readonly currencyService = inject(CurrencyService);
  private readonly lotIncentiveService = inject(DsmLotIncentiveService);

  readonly addendumId = input.required<string>();
  readonly currentAddendumIncentives = input<number>(0);

  readonly poolTotal = this.lotIncentiveService.incentiveTotal;
  readonly appliedElsewhere = computed(
    () => this.lotIncentiveService.incentiveApplied() - this.currentAddendumIncentives()
  );

  readonly maxApplicable = computed(() => this.poolTotal() - this.appliedElsewhere());

  readonly currencyCode = this.currencyService.currencyCode;
  readonly currencySymbol = this.currencyService.currencySymbol;

  readonly methodOptions: { label: string; value: LotAddendumIncentiveApplicationMethod; description: string }[] = [
    {
      label: 'Highest cost first',
      value: LotAddendumIncentiveApplicationMethod['Highest cost first'],
      description: 'Fills the most expensive option first until the amount runs out.'
    },
    {
      label: 'Order added',
      value: LotAddendumIncentiveApplicationMethod['Order added'],
      description: 'Fills options in the order they were added to the addendum.'
    }
  ];

  @Output() formSaved = new EventEmitter<void>();
  @Output() formCancel = new EventEmitter<void>();

  form: FormGroup;
  readonly isSubmitting = signal(false);

  /**
   * Debounced form value used to drive the preview API call so we don't fire
   * a request on every keystroke / radio toggle.
   */
  private readonly debouncedFormValue: Signal<{
    amount: number | null;
    method: LotAddendumIncentiveApplicationMethod;
  }>;
  readonly previewAmount = computed<number | null>(() => {
    const amount = this.debouncedFormValue()?.amount ?? null;
    const max = this.maxApplicable();
    if (amount === null || amount === undefined || amount <= 0 || amount > max) {
      return null;
    }
    return amount;
  });
  readonly previewMethod = computed<LotAddendumIncentiveApplicationMethod>(
    () => this.debouncedFormValue()?.method ?? LotAddendumIncentiveApplicationMethod['Highest cost first']
  );

  readonly formAmount: Signal<number | null>;
  readonly isFormInvalid: Signal<boolean>;
  readonly isSubmitDisabled = computed(() => this.isFormInvalid() || this.isSubmitting());
  readonly submitButtonLabel = computed(() => {
    const amount = this.formAmount();
    if (amount != null && amount > 0) {
      return `Apply ${this.currencyService.formatCurrency(amount)}`;
    }
    return 'Apply';
  });

  constructor() {
    this.form = this.fb.group({
      amount: [null as number | null, [Validators.required, Validators.min(0.01)]],
      method: [LotAddendumIncentiveApplicationMethod['Highest cost first'], [Validators.required]]
    });

    this.formAmount = toSignal(
      this.form.valueChanges.pipe(
        startWith(this.form.value),
        map(v => v.amount as number | null)
      ),
      { initialValue: null as number | null }
    );
    this.isFormInvalid = toSignal(
      this.form.statusChanges.pipe(
        startWith(this.form.status),
        map(status => status !== 'VALID')
      ),
      { initialValue: true }
    );

    this.debouncedFormValue = toSignal(this.form.valueChanges.pipe(startWith(this.form.value), debounceTime(400)), {
      initialValue: this.form.value
    });

    effect(() => {
      const max = this.maxApplicable();
      this.form.get('amount')?.setValidators([Validators.required, Validators.min(0.01), Validators.max(max)]);
      this.form.get('amount')?.updateValueAndValidity();
    });
  }

  setMaxAmount(): void {
    this.form.get('amount')?.setValue(this.maxApplicable());
    this.form.get('amount')?.markAsTouched();
  }

  submit(): void {
    if (this.form.invalid || this.isSubmitting()) {
      this.form.markAllAsTouched();
      return;
    }

    const addendumId = this.addendumId();
    if (!addendumId) return;

    const val = this.form.getRawValue();
    const payload: DsmApplyAddendumIncentivePayload = {
      amount: val.amount as number,
      method: val.method as LotAddendumIncentiveApplicationMethod
    };

    this.isSubmitting.set(true);
    this.lotIncentiveService
      .applyIncentive(addendumId, payload)
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: () => {
          this.formSaved.emit();
        }
      });
  }

  onCancel(): void {
    this.formCancel.emit();
  }
}
